import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, getDocs } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import LandingNavbar from "../components/landingNavbar";
import Footer from "../components/footer";
import LoadingSpinner from "../components/loadingSpinner";
import { createDesignFromTemplate } from "../services/createDesignFromTemplate";
import { toast } from "react-hot-toast";
import { PENDING_TEMPLATE_STORAGE_KEY } from "../utils/navigation";

const DEFAULT_THUMB =
  "https://firebasestorage.googleapis.com/v0/b/cloud-drill-design.appspot.com/o/snapshots%2FuHksLWYzwdcq09JNvegv.png?alt=media&token=4e69d0f9-99e4-48e8-ba82-71e55f306830";

const CATEGORIES = ["All", "Opener", "Ballad", "Closer", "Parade"];

function buildAuthRedirect(pathname) {
  const q = `?redirect=${encodeURIComponent(pathname)}`;
  return { login: `/login${q}`, register: `/register${q}` };
}

export default function AllTemplatesPage() {
  const [user, authLoading] = useAuthState(auth);
  const [templates, setTemplates] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [creating, setCreating] = useState(false);

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const navigate = useNavigate();

  // Fetch templates
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const snapshot = await getDocs(collection(db, "drillTemplates"));
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (!cancelled) setTemplates(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) setLoadError("fetch_failed");
      } finally {
        if (!cancelled) setFetching(false);
      }
    }

    load();
    return () => (cancelled = true);
  }, []);

  // Filtered templates
  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const matchesCategory =
        activeCategory === "All" || t.category === activeCategory;

      const matchesSearch =
        t.title?.toLowerCase().includes(search.toLowerCase()) ||
        t.description?.toLowerCase().includes(search.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [templates, search, activeCategory]);

  // Handle template usage
  const handleUseTemplate = async (template) => {
    if (!user) {
      localStorage.setItem(PENDING_TEMPLATE_STORAGE_KEY, template.id);
      const { login } = buildAuthRedirect("/templates");
      navigate(login);
      return;
    }

    try {
      setCreating(true);
      const designId = await createDesignFromTemplate(template, user.uid);
      toast.success("Design created!");
      navigate(`/editor/${designId}`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to create design");
    } finally {
      setCreating(false);
    }
  };

  // Loading
  if (fetching || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex flex-col">
        <LandingNavbar />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Error
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex flex-col">
        <LandingNavbar />
        <div className="flex-1 flex items-center justify-center text-center">
          <div>
            <h1 className="text-xl font-semibold mb-2">
              Failed to load templates
            </h1>
            <p className="text-gray-500">Try again later.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 flex flex-col">
      <Helmet>
        <title>DrillFlo Templates</title>
        <meta
          name="description"
          content="Browse DrillFlo marching band drill templates."
        />
      </Helmet>

      <LandingNavbar />

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        {/* HERO */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Start Your Show Faster
          </h1>
          <p className="text-gray-600 mb-6">
            Pre-built drill templates designed for real marching bands.
          </p>

          <input
            type="text"
            placeholder="Search templates (opener, ballad, parade...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md mx-auto px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        {/* CATEGORY FILTER */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 text-sm rounded-full border ${activeCategory === cat
                ? "bg-blue-600 text-white border-blue-600"
                : "border-gray-300 hover:bg-gray-100"
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition"
            >
              {/* IMAGE */}
              <div className="aspect-video bg-gray-100 overflow-hidden">
                <img
                  src={template.thumbnail || DEFAULT_THUMB}
                  alt={template.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition"
                />
              </div>

              {/* CONTENT */}
              <div className="p-4">
                <p className="text-xs text-blue-600 font-medium mb-1">
                  {template.category || "Drill Template"}
                </p>

                <h2 className="text-sm font-semibold text-gray-900 line-clamp-2">
                  {template.title}
                </h2>

                <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                  {template.description}
                </p>

                <button
                  onClick={() => navigate(`/templates/${template.id}`)}
                  disabled={creating}
                  className="mt-3 w-full text-sm bg-blue-600 text-white py-2 rounded-lg opacity-0 group-hover:opacity-100 transition disabled:opacity-50"
                >
                  View Template
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* EMPTY STATE */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            No templates found.
          </div>
        )}

        {/* BACK LINK */}
        <p className="mt-10 text-center">
          <Link
            to="/marching-band-drill-design-software"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            ← Back to DrillFlo
          </Link>
        </p>
      </main>

      <Footer />
    </div>
  );
}